import { rootApi } from '../rootApi';
import { HTTPRequestType, PaginatedResponse } from '../api.types';
import { SendSingleMailDto, CompleteConnectionDto, EmailConnectionStatus, UpdateMailSettingsDto, EmailMessageTemplate, EmailMessageTemplateType, CreateMessageTemplateDto, SendMultiMailDto, EmailActivity, InitiateConnectionDto } from './email-sync.types';

export const emailSyncApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getEmailConnectionState: builder.query<{ id: string, status: EmailConnectionStatus }, void>({
      query: () => ({
        url: `/email-sync/connection/status`,
      }),
      providesTags: ['EmailConnection']
    }),

    initiateEmailConnection: builder.mutation<{ url: string }, InitiateConnectionDto>({
      query: (data) => ({
        url: `/email-sync/connection/initiate`,
        method: HTTPRequestType.POST,
        data
      })
    }),

    completeEmailConnection: builder.mutation<void, CompleteConnectionDto>({
      query: (data) => ({
        url: `/email-sync/connection/complete`,
        method: HTTPRequestType.POST,
        data
      }),
      invalidatesTags: ['EmailConnection']
    }),

    disconnectEmailConnection: builder.mutation<void, void>({
      query: () => ({
        url: `/email-sync/connection/disconnect`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['EmailConnection']
    }),

    // mailer endpoints
    sendSingleEmail: builder.mutation<void, SendSingleMailDto>({
      query: (data) => ({
        url: `/email-sync/mailer/single-email`,
        method: HTTPRequestType.POST,
        data
      })
    }),
    sendMultiEmail: builder.mutation<void, SendMultiMailDto>({
      query: (data) => ({
        url: `/email-sync/mailer/multi-email`,
        method: HTTPRequestType.POST,
        data
      })
    }),

    getMailerSettings: builder.query<{ signature?: string }, void>({
      query: () => ({
        url: `/email-sync/mailer/settings`,
      }),
      providesTags: ['MailerSettings']
    }),
    updateMailerSettings: builder.mutation<void, UpdateMailSettingsDto>({
      query: (data) => ({
        url: `/email-sync/mailer/settings`,
        method: HTTPRequestType.POST,
        data
      }),
      invalidatesTags: ['MailerSettings']
    }),

    // activities
    getEmailActivities: builder.query<PaginatedResponse<EmailActivity>, { page: number; per_page: number, search_query?: string }>({
      query: ({ page, per_page, search_query }) => ({
        url: `/email-sync/mailer/activities`,
        params: { page, per_page, search_query },
      }),
      providesTags: (result) => ['EmailActivities'],
    }),

    // email message templates
    getEmailMessageTemplates: builder.query<PaginatedResponse<EmailMessageTemplate>, { page: number; per_page: number, type?: EmailMessageTemplateType, search_query?: string }>({
      query: ({ page, per_page, type, search_query }) => ({
        url: `/email-sync/message-templates`,
        params: { page, per_page, type, search_query },
      }),
      providesTags: (result) => ['EmailMessageTemplates'],
    }),
    getTemplatedContent: builder.query<{ subject: string, content: string }, { templateId: string, entityId: string }>({
      query: ({ templateId, entityId }) => ({
        url: `/email-sync/message-templates/${templateId}/content`,
        params: { entity_id: entityId },
      }),
    }),
    createEmailMessageTemplate: builder.mutation<EmailMessageTemplate, CreateMessageTemplateDto>({
      query: (data) => ({
        url: `/email-sync/message-templates`,
        method: HTTPRequestType.POST,
        data
      }),
      invalidatesTags: ['EmailMessageTemplates'],
    }),
    updateEmailMessageTemplate: builder.mutation<EmailMessageTemplate, { id: string; data: Partial<CreateMessageTemplateDto> }>({
      query: ({ id, data }) => ({
        url: `/email-sync/message-templates/${id}`,
        method: HTTPRequestType.PUT,
        data
      }),
      invalidatesTags: ['EmailMessageTemplates'],
    }),
    deleteEmailMessageTemplate: builder.mutation<void, string>({
      query: (id) => ({
        url: `/email-sync/message-templates/${id}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: ['EmailMessageTemplates'],
    }),


  }),
});

export const {
  useGetEmailConnectionStateQuery,
  useInitiateEmailConnectionMutation,
  useCompleteEmailConnectionMutation,
  useDisconnectEmailConnectionMutation,

  useSendSingleEmailMutation,
  useSendMultiEmailMutation,

  useGetMailerSettingsQuery,
  useUpdateMailerSettingsMutation,

  useGetEmailActivitiesQuery,

  useGetEmailMessageTemplatesQuery,
  useGetTemplatedContentQuery,
  useLazyGetTemplatedContentQuery,
  useCreateEmailMessageTemplateMutation,
  useUpdateEmailMessageTemplateMutation,
  useDeleteEmailMessageTemplateMutation,
} = emailSyncApi;
